import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'primaryOnDark' | 'secondaryOnDark' | 'ghostOnDark'

const base =
  'group inline-flex items-center gap-2 text-sm font-medium tracking-tight transition-colors duration-300'

// The "OnDark" variants are for use inside the fixed-dark statement panels
// (Footer, FinalCTA, ProductsFeature) — those panels stay dark in both
// themes, so their buttons use the theme-invariant night/snow tokens
// instead of ink/paper, which swap with the site theme.
const variants: Record<Variant, string> = {
  primary: 'rounded-full bg-ink px-6 py-3 text-paper hover:bg-signal',
  secondary: 'rounded-full border border-ink/15 px-6 py-3 text-ink hover:border-ink',
  ghost: 'text-ink hover:text-signal',
  primaryOnDark: 'rounded-full bg-snow px-6 py-3 text-night hover:bg-signal',
  secondaryOnDark: 'rounded-full border border-snow/20 px-6 py-3 text-snow hover:border-snow',
  ghostOnDark: 'text-snow hover:text-signal',
}

const ghostVariants = new Set<Variant>(['ghost', 'ghostOnDark'])

export function CTA({
  to,
  href,
  variant = 'primary',
  children,
  className = '',
  onClick,
}: {
  to?: string
  href?: string
  variant?: Variant
  children: ReactNode
  className?: string
  onClick?: () => void
}) {
  const classes = `${base} ${variants[variant]} ${className}`
  const arrow = (
    <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
  )

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={classes} onClick={onClick}>
        {children}
        {ghostVariants.has(variant) ? arrow : null}
      </a>
    )
  }

  return (
    <Link to={to ?? '/'} className={classes} onClick={onClick}>
      {children}
      {variant === 'ghost' ? arrow : null}
    </Link>
  )
}
